import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import { ActivityIndicator, Image, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import {
  castJuryVote,
  disputeTagLabel,
  getDisputeEvidenceSignedUrl,
  getJuryDisputeMessages,
  getMyJuryDuties,
  type JuryDisputeMessage,
  type JuryDuty,
  type JuryProfileStats,
} from '@/lib/disputes';
import { ZoomableImageModal } from '@/components/ZoomableImageModal';
import { formatDateTime } from '@/lib/format';

// Σταθερά χρώματα ανά πλευρά — ίδια παντού (κάρτα υπόθεσης, κουμπιά ψήφου,
// φούσκες chat) ώστε ο κριτής να ξεχωρίζει αμέσως ποιος είναι ποιος.
// Ουδέτερα χρώματα (χωρίς σύνδεση ενοχής/βαρύτητας) — μπλε/μοβ, όχι
// μπλε/πορτοκαλί-κόκκινο, ώστε να μη γέρνει η ζυγαριά υπέρ/κατά κάποιου
// πριν καν διαβάσει ο κριτής τα στοιχεία.
const SIDE_COLOR_INITIATOR = '#60A5FA';
const SIDE_COLOR_ACCUSED = '#A78BFA';

/** Read-only θάμπναϊλ evidence φωτογραφίας για τον κριτή — lazy signed URL. */
function JuryEvidenceThumb({ path, onPress, styles }: {
  path: string;
  onPress: (uri: string) => void;
  styles: ReturnType<typeof makeStyles>;
}) {
  const [uri, setUri] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void getDisputeEvidenceSignedUrl(path).then((u) => { if (alive) setUri(u); }).catch(() => {});
    return () => { alive = false; };
  }, [path]);
  return (
    <Pressable onPress={() => uri && onPress(uri)} accessibilityRole="button">
      {uri ? (
        <Image source={{ uri }} style={styles.evidenceThumb} />
      ) : (
        <View style={[styles.evidenceThumb, styles.evidenceThumbLoading]}>
          <ActivityIndicator size="small" color="#fff" />
        </View>
      )}
    </Pressable>
  );
}

export default function JuryDutyScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();

  const [duties, setDuties] = useState<JuryDuty[]>([]);
  const [loading, setLoading] = useState(true);
  const [votingId, setVotingId] = useState<string | null>(null);
  const [chatDisputeId, setChatDisputeId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<JuryDisputeMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [viewerUri, setViewerUri] = useState<string | null>(null);

  async function openChat(disputeId: string) {
    setChatDisputeId(disputeId);
    setChatLoading(true);
    setChatError(null);
    setChatMessages([]);
    try {
      setChatMessages(await getJuryDisputeMessages(disputeId));
    } catch (e) {
      setChatError(getErrorMessage(e, t('jury.chatLoadError')));
    } finally {
      setChatLoading(false);
    }
  }

  const load = useCallback(() => {
    setLoading(true);
    void getMyJuryDuties()
      .then((list) => setDuties(list.filter((d) => !d.already_voted)))
      .catch(() => setDuties([]))
      .finally(() => setLoading(false));
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  async function vote(disputeId: string, voteForInitiator: boolean) {
    setVotingId(disputeId);
    try {
      await castJuryVote(disputeId, voteForInitiator);
      setDuties((prev) => prev.filter((d) => d.dispute_id !== disputeId));
    } catch (e) {
      appAlert(t('common.error'), getErrorMessage(e, t('jury.voteFailed')));
    } finally {
      setVotingId(null);
    }
  }

  function confirmVote(duty: JuryDuty, voteForInitiator: boolean) {
    const share = duty.jury_size > 0 ? Math.floor(duty.stake_amount / duty.jury_size) : duty.stake_amount;
    appAlert(
      t('jury.confirmTitle'),
      t('jury.confirmBody', { amount: share }),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('common.confirm'), onPress: () => void vote(duty.dispute_id, voteForInitiator) },
      ],
    );
  }

  return (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View style={styles.headerTitle}>
          <Ionicons name="people-outline" size={20} color={colors.text} />
          <Text style={styles.headerText}>{t('jury.title')}</Text>
        </View>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + spacing.xl }]}>
        <View style={styles.introBanner}>
          <Ionicons name="eye-off-outline" size={16} color={colors.primary} />
          <Text style={styles.introText}>{t('jury.introAnonymous')}</Text>
        </View>

        <View style={styles.howToBanner}>
          <Text style={styles.howToTitle}>{t('jury.howToJudgeTitle')}</Text>
          <Text style={styles.howToText}>{t('jury.howToJudgeBody')}</Text>
        </View>

        {loading ? (
          <ActivityIndicator color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : duties.length === 0 ? (
          <View style={styles.empty}>
            <Ionicons name="checkmark-done-outline" size={40} color={colors.textMuted} />
            <Text style={styles.emptyText}>{t('jury.empty')}</Text>
          </View>
        ) : (
          duties.map((d) => (
            <View key={d.dispute_id} style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>{d.agreement_title}</Text>
                <Text style={styles.cardStake}>
                  {t('jury.caseStake', { amount: d.stake_amount, share: d.jury_size > 0 ? Math.floor(d.stake_amount / d.jury_size) : d.stake_amount })}
                </Text>
                <Text style={styles.cardDeadline}>{t('jury.myDeadline', { date: formatDateTime(d.my_vote_deadline) })}</Text>
              </View>

              <View style={[styles.sideBlock, styles.sideBlockInitiator]}>
                <View style={styles.sideLabelRow}>
                  <Text style={[styles.sideLabel, styles.sideLabelInitiator]}>{t('jury.initiatorSide')}</Text>
                  <View style={[styles.openedBadge, styles.openedBadgeInitiator]}>
                    <Text style={[styles.openedBadgeText, styles.sideLabelInitiator]}>{t('jury.openedDisputeBadge')}</Text>
                  </View>
                </View>
                <View style={styles.tags}>
                  {d.initiator_tags.length > 0
                    ? d.initiator_tags.map((tag) => (
                        <View key={tag} style={[styles.tagPill, styles.tagPillInitiator]}>
                          <Text style={styles.tagText}>{disputeTagLabel(tag)}</Text>
                        </View>
                      ))
                    : <Text style={styles.noTags}>{t('jury.noStatement')}</Text>}
                </View>
                {!!d.initiator_statement && <Text style={styles.statementText}>&ldquo;{d.initiator_statement}&rdquo;</Text>}
                {!!d.initiator_evidence_urls?.length && (
                  <View style={styles.evidenceGrid}>
                    {d.initiator_evidence_urls.map((path) => (
                      <JuryEvidenceThumb key={path} path={path} onPress={setViewerUri} styles={styles} />
                    ))}
                  </View>
                )}
                <ProfileStatsRow stats={d.initiator_stats} styles={styles} t={t} />
                <View style={styles.voteBtnInline}>
                  <Button
                    title={t('jury.voteInitiator')}
                    variant="secondary"
                    accentColor={SIDE_COLOR_INITIATOR}
                    loading={votingId === d.dispute_id}
                    onPress={() => confirmVote(d, true)}
                  />
                </View>
              </View>

              <View style={styles.sideDivider} />

              <View style={[styles.sideBlock, styles.sideBlockAccused]}>
                <Text style={[styles.sideLabel, styles.sideLabelAccused]}>{t('jury.accusedSide')}</Text>
                <View style={styles.tags}>
                  {d.accused_tags.length > 0
                    ? d.accused_tags.map((tag) => (
                        <View key={tag} style={[styles.tagPill, styles.tagPillAccused]}>
                          <Text style={styles.tagText}>{disputeTagLabel(tag)}</Text>
                        </View>
                      ))
                    : <Text style={styles.noTags}>{t('jury.noStatement')}</Text>}
                </View>
                {!!d.accused_statement && <Text style={styles.statementText}>&ldquo;{d.accused_statement}&rdquo;</Text>}
                {!!d.accused_evidence_urls?.length && (
                  <View style={styles.evidenceGrid}>
                    {d.accused_evidence_urls.map((path) => (
                      <JuryEvidenceThumb key={path} path={path} onPress={setViewerUri} styles={styles} />
                    ))}
                  </View>
                )}
                <ProfileStatsRow stats={d.accused_stats} styles={styles} t={t} />
                <View style={styles.voteBtnInline}>
                  <Button
                    title={t('jury.voteAccused')}
                    variant="secondary"
                    accentColor={SIDE_COLOR_ACCUSED}
                    loading={votingId === d.dispute_id}
                    onPress={() => confirmVote(d, false)}
                  />
                </View>
              </View>

              <Pressable style={styles.viewChatBtn} onPress={() => void openChat(d.dispute_id)} accessibilityRole="button">
                <Ionicons name="chatbubbles-outline" size={15} color={colors.primary} />
                <Text style={styles.viewChatText}>{t('jury.viewChat')}</Text>
              </Pressable>
            </View>
          ))
        )}
      </ScrollView>

      <Modal visible={!!chatDisputeId} animationType="slide" transparent onRequestClose={() => setChatDisputeId(null)}>
        <View style={styles.chatOverlay}>
          <View style={[styles.chatSheet, { paddingBottom: insets.bottom + spacing.md }]}>
            <View style={styles.chatHeader}>
              <Text style={styles.chatHeaderText}>{t('jury.chatModalTitle')}</Text>
              <Pressable onPress={() => setChatDisputeId(null)} hitSlop={10} accessibilityRole="button">
                <Ionicons name="close" size={22} color={colors.text} />
              </Pressable>
            </View>
            <ScrollView style={styles.chatBody} contentContainerStyle={{ gap: spacing.sm }}>
              {chatLoading ? (
                <ActivityIndicator color={colors.primary} style={{ marginTop: spacing.lg }} />
              ) : chatError ? (
                <Text style={styles.chatErrorText}>{chatError}</Text>
              ) : chatMessages.length === 0 ? (
                <Text style={styles.chatEmptyText}>{t('jury.chatEmpty')}</Text>
              ) : (
                chatMessages.map((m, i) => (
                  <View
                    key={i}
                    style={[
                      styles.chatBubble,
                      m.side === 'initiator' ? styles.chatBubbleInitiator : m.side === 'accused' ? styles.chatBubbleAccused : styles.chatBubbleOther,
                    ]}
                  >
                    <Text style={styles.chatBubbleSide}>
                      {m.side === 'initiator' ? t('jury.chatSideInitiator') : m.side === 'accused' ? t('jury.chatSideAccused') : t('jury.chatSideOther')}
                    </Text>
                    <Text style={styles.chatBubbleBody}>{m.body}</Text>
                    {m.read_by_recipient != null && (
                      <Text style={styles.chatBubbleReadStatus}>
                        {m.read_by_recipient ? t('jury.chatReadByOther') : t('jury.chatUnreadByOther')}
                      </Text>
                    )}
                  </View>
                ))
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <ZoomableImageModal uri={viewerUri} onClose={() => setViewerUri(null)} />
    </View>
  );
}

/** Ανώνυμα στατιστικά ενός συμβαλλόμενου — ΧΩΡΙΣ όνομα/φωτογραφία, μόνο αριθμοί. */
function ProfileStatsRow({ stats, styles, t }: { stats: JuryProfileStats; styles: ReturnType<typeof makeStyles>; t: (key: string, opts?: Record<string, unknown>) => string }) {
  if (!stats) return null;
  return (
    <View style={styles.statsGrid}>
      <View style={styles.statCell}>
        <Text style={styles.statValue}>{stats.effective_rating.toFixed(1)}★</Text>
        <Text style={styles.statLabel}>{t('jury.statRating', { count: stats.review_count })}</Text>
      </View>
      <View style={styles.statCell}>
        <Text style={styles.statValue}>{stats.completed_count}</Text>
        <Text style={styles.statLabel}>{t('jury.statCompleted')}</Text>
      </View>
      <View style={styles.statCell}>
        <Text style={[styles.statValue, stats.cancelled_unilateral_count > 0 && styles.statValueWarning]}>
          {stats.cancelled_unilateral_count}
        </Text>
        <Text style={styles.statLabel}>{t('jury.statUnilateralCancel')}</Text>
      </View>
      <View style={styles.statCell}>
        <Text style={[styles.statValue, stats.disputes_opened_count > 2 && styles.statValueWarning]}>
          {stats.disputes_opened_count}
        </Text>
        <Text style={styles.statLabel}>{t('jury.statDisputesOpened')}</Text>
      </View>
      {stats.dispute_win_rate_flagged && (
        <View style={styles.flaggedPill}>
          <Ionicons name="information-circle-outline" size={12} color="#F59E0B" />
          <Text style={styles.flaggedText}>{t('jury.statFlagged')}</Text>
        </View>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    headerTitle: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    headerText: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    content: { padding: spacing.md, gap: spacing.md },
    introBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: 'rgba(201,168,67,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(201,168,67,0.25)',
      borderRadius: radius.md,
      padding: spacing.sm,
    },
    introText: { flex: 1, color: colors.text, fontSize: fontSize.sm, lineHeight: 18 },
    howToBanner: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.sm,
      gap: 4,
    },
    howToTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    howToText: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 18 },
    viewChatBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 8,
    },
    viewChatText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    chatOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
    chatSheet: {
      backgroundColor: colors.background,
      borderTopLeftRadius: radius.lg,
      borderTopRightRadius: radius.lg,
      maxHeight: '75%',
      paddingHorizontal: spacing.md,
      paddingTop: spacing.md,
    },
    chatHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: spacing.sm,
    },
    chatHeaderText: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    chatBody: { flexGrow: 0 },
    chatErrorText: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center', marginTop: spacing.lg },
    chatEmptyText: { color: colors.textMuted, fontSize: fontSize.sm, textAlign: 'center', marginTop: spacing.lg },
    chatBubble: { borderRadius: radius.md, padding: spacing.sm, gap: 2, maxWidth: '88%' },
    chatBubbleInitiator: { backgroundColor: 'rgba(96,165,250,0.16)', borderColor: SIDE_COLOR_INITIATOR, borderWidth: 1, alignSelf: 'flex-start' },
    chatBubbleAccused: { backgroundColor: 'rgba(167,139,250,0.16)', borderColor: SIDE_COLOR_ACCUSED, borderWidth: 1, alignSelf: 'flex-end' },
    chatBubbleOther: { backgroundColor: colors.surfaceAlt, alignSelf: 'center' },
    chatBubbleSide: { color: colors.textMuted, fontSize: 10, fontWeight: '700', textTransform: 'uppercase' },
    chatBubbleBody: { color: colors.text, fontSize: fontSize.sm, lineHeight: 18 },
    chatBubbleReadStatus: { color: colors.textMuted, fontSize: 9.5, marginTop: 3, fontStyle: 'italic' },
    empty: { alignItems: 'center', gap: spacing.md, paddingVertical: spacing.xl },
    emptyText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center' },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    cardHeader: { gap: 2 },
    cardTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    cardStake: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600' },
    cardDeadline: { color: colors.textMuted, fontSize: fontSize.xs },
    sideBlock: {
      gap: 6,
      padding: spacing.sm,
      borderRadius: radius.md,
      borderWidth: 1,
    },
    sideBlockInitiator: { backgroundColor: 'rgba(96,165,250,0.08)', borderColor: 'rgba(96,165,250,0.3)' },
    sideBlockAccused: { backgroundColor: 'rgba(167,139,250,0.08)', borderColor: 'rgba(167,139,250,0.3)' },
    sideDivider: { height: spacing.md },
    statsGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.xs,
      marginTop: 2,
    },
    statCell: {
      minWidth: 68,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      paddingVertical: 6,
      paddingHorizontal: spacing.xs,
      alignItems: 'center',
      gap: 1,
    },
    statValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    statValueWarning: { color: colors.danger },
    statLabel: { color: colors.textMuted, fontSize: 10, textAlign: 'center', lineHeight: 12 },
    flaggedPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      backgroundColor: 'rgba(245,158,11,0.12)',
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    flaggedText: { color: '#F59E0B', fontSize: 10, fontWeight: '600' },
    sideLabel: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 1 },
    sideLabelInitiator: { color: SIDE_COLOR_INITIATOR },
    sideLabelAccused: { color: SIDE_COLOR_ACCUSED },
    sideLabelRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap' },
    openedBadge: {
      borderRadius: radius.full,
      borderWidth: 1,
      paddingVertical: 2,
      paddingHorizontal: spacing.xs,
    },
    openedBadgeInitiator: { borderColor: SIDE_COLOR_INITIATOR, backgroundColor: 'rgba(96,165,250,0.12)' },
    openedBadgeText: { fontSize: 9.5, fontWeight: '700', textTransform: 'none', letterSpacing: 0 },
    tags: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    tagPill: {
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      backgroundColor: colors.surfaceAlt,
    },
    tagPillInitiator: { borderColor: SIDE_COLOR_INITIATOR },
    tagPillAccused: { borderColor: SIDE_COLOR_ACCUSED },
    tagText: { color: colors.text, fontSize: 12, fontWeight: '600' },
    noTags: { color: colors.textMuted, fontSize: 12, fontStyle: 'italic' },
    statementText: { color: colors.text, fontSize: fontSize.sm, fontStyle: 'italic', lineHeight: 18, marginTop: 2 },
    evidenceGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: 2 },
    evidenceThumb: { width: 64, height: 64, borderRadius: radius.md, backgroundColor: colors.surfaceAlt },
    evidenceThumbLoading: { alignItems: 'center', justifyContent: 'center' },
    voteBtnInline: { marginTop: spacing.xs },
  });
}
